import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, EventEmitter, Injectable, Type } from '@angular/core';
import { FikoDialogCore } from './fiko-dialog-core.component';

export interface DialogZIndexInfo {
  componentInstance: FikoDialogCore;
  zIndex: number;
  acilisZamani: number;
  sonTiklamaZamani: number;
}

/**
 * Dialog kapanma event'i - menülerin de kapanması için kullanılır
 */
export interface DialogKapanmaEvent {
  dialogId: string;
  dialogInstance: FikoDialogCore;
}

@Injectable({
  providedIn: 'root'
})
export class FikoDialogService {
  // ID bazlı dialog cache'i - her benzersiz ID için bir dialog instance
  private dialogCache: Map<string, ComponentRef<FikoDialogCore>> = new Map();

  // Component türüne göre varsayılan dialog cache'i (geriye uyumluluk için)
  private dialogTurCache: Map<Type<any>, ComponentRef<FikoDialogCore>> = new Map();

  // Z-index takip sistemi
  private readonly TEMEL_Z_INDEX = 1000;
  private readonly Z_INDEX_ARTIS = 10;
  private mevcutZIndex = this.TEMEL_Z_INDEX;
  private aktifDialoglar: Map<FikoDialogCore, DialogZIndexInfo> = new Map();
  private dialogSirasi: FikoDialogCore[] = [];

  /**
   * Dialog kapandığında yayınlanan event
   * FikoThreeJsComponent bu event'i dinleyerek 3D hızlı menü ve context menüleri kapatır
   */
  dialogKapandi$ = new EventEmitter<DialogKapanmaEvent>();

  /**
   * Mevcut en yüksek z-index değerini döndürür (read-only)
   * Overlay'ler bu değerin üstünde olmalı
   */
  get aktifZIndex(): number {
    return this.mevcutZIndex;
  }

  /**
   * Overlay/menü için yeni z-index al
   * Sayfadaki tüm elementlerin z-index'ini tarar ve en yüksek olanı +1 döndürür
   * Sağ tuş menüsü, 3D hızlı menü vb. için
   */
  overlayZIndexAl(): number {
    // Sayfadaki en yüksek z-index'i bul
    let maxZIndex = this.mevcutZIndex;
    let maxElement: HTMLElement | null = null;

    // Tüm elementleri tara - hem computed hem inline style kontrol et
    const allElements = document.querySelectorAll('*');
    allElements.forEach((el: Element) => {
      const htmlEl = el as HTMLElement;

      // 1. Computed style kontrol
      try {
        const computedStyle = window.getComputedStyle(htmlEl);
        const computedZ = parseInt(computedStyle.zIndex, 10);
        if (!isNaN(computedZ) && computedZ > maxZIndex) {
          maxZIndex = computedZ;
          maxElement = htmlEl;
        }
      } catch (e) {}

      // 2. Inline style kontrol
      if (htmlEl.style?.zIndex) {
        const inlineZ = parseInt(htmlEl.style.zIndex, 10);
        if (!isNaN(inlineZ) && inlineZ > maxZIndex) {
          maxZIndex = inlineZ;
          maxElement = htmlEl;
        }
      }

      // 3. Attribute kontrol (style="z-index: X")
      const styleAttr = htmlEl.getAttribute('style');
      if (styleAttr) {
        const match = styleAttr.match(/z-index\s*:\s*(\d+)/i);
        if (match) {
          const attrZ = parseInt(match[1], 10);
          if (!isNaN(attrZ) && attrZ > maxZIndex) {
            maxZIndex = attrZ;
            maxElement = htmlEl;
          }
        }
      }
    });

    // En yüksek z-index + 10 döndür (güvenlik marjı)
    return maxZIndex + 10;
  }

  // ID sayacı - benzersiz ID üretmek için
  private dialogIdSayaci = 0;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {}

  /**
   * Yeni dialog açıldığında çağrılır
   * En üstteki dialog için en yüksek z-index değerini döndürür
   */
  dialogAcildi(componentInstance: FikoDialogCore): number {
    const yeniZIndex = this.mevcutZIndex + this.Z_INDEX_ARTIS;
    this.mevcutZIndex = yeniZIndex;

    const dialogInfo: DialogZIndexInfo = {
      componentInstance,
      zIndex: yeniZIndex,
      acilisZamani: Date.now(),
      sonTiklamaZamani: Date.now()
    };

    this.aktifDialoglar.set(componentInstance, dialogInfo);
    this.dialogSirasi.push(componentInstance);

        return yeniZIndex;
  }

  /**
   * Dialog tıklandığında çağrılır
   * Tıklanan dialog'u en üste taşır
   */
  dialogTiklandi(componentInstance: FikoDialogCore): number {
    const dialog = this.aktifDialoglar.get(componentInstance);
    if (!dialog) {
      // Dialog daha önce açılmamışsa, yeni olarak ekle
      return this.dialogAcildi(componentInstance);
    }

    // Z-index'i güncelle ve en üste taşı
    const yeniZIndex = this.mevcutZIndex + this.Z_INDEX_ARTIS;
    this.mevcutZIndex = yeniZIndex;

    dialog.zIndex = yeniZIndex;
    dialog.sonTiklamaZamani = Date.now();

    // Sıralamayı güncelle - tıklanan dialog'u en sona taşı
    const siralamaIndeksi = this.dialogSirasi.indexOf(componentInstance);
    if (siralamaIndeksi > -1) {
      this.dialogSirasi.splice(siralamaIndeksi, 1);
      this.dialogSirasi.push(componentInstance);
    }

        return yeniZIndex;
  }

  /**
   * Dialog kapandığında çağrılır
   * Dialog'u sistemden kaldırır ve kapanma event'i yayınlar
   */
  dialogKapandi(componentInstance: FikoDialogCore): void {
    const dialog = this.aktifDialoglar.get(componentInstance);
    if (dialog) {
      this.aktifDialoglar.delete(componentInstance);

      // Sıralamadan da kaldır
      const siralamaIndeksi = this.dialogSirasi.indexOf(componentInstance);
      if (siralamaIndeksi > -1) {
        this.dialogSirasi.splice(siralamaIndeksi, 1);
      }

      // Dialog kapanma event'i yayınla - 3D hızlı menü ve context menü kapansın
      this.dialogKapandi$.emit({
        dialogId: componentInstance.id_dialog,
        dialogInstance: componentInstance
      });
    }
  }

  /**
   * Dialog'un mevcut z-index değerini döndürür
   */
  getZIndex(componentInstance: FikoDialogCore): number | null {
    const dialog = this.aktifDialoglar.get(componentInstance);
    return dialog ? dialog.zIndex : null;
  }

  /**
   * En üstteki dialog'un bilgisini döndürür (son açılan/tıklanan)
   */
  enUsttekiDialog(): FikoDialogCore | null {
    if (this.dialogSirasi.length === 0) {
      return null;
    }

    return this.dialogSirasi[this.dialogSirasi.length - 1];
  }

  /**
   * Dialog açar
   *
   * **İki mod:**
   * 1. **ID yok** → Component türüne göre (varsa günceller, yoksa oluşturur) - ESKİ DAVRANIŞ
   * 2. **ID var** → O ID'ye göre (varsa günceller, yoksa yeni oluşturur) - YENİ DAVRANIŞ
   *
   * @param id - Opsiyonel: Belirli bir ID ile dialog aç
   * @returns Dialog instance
   */
  ac({ bilesen, ayarlar = null, parametreler = null, $event = null, id = null }: {
    bilesen: Type<any>,
    ayarlar?: any,
    parametreler?: any,
    $event?: any,
    id?: string | null
  }): FikoDialogCore {
    let componentRef: ComponentRef<FikoDialogCore> | undefined;

    // ID verilmişse ID'ye göre ara, yoksa component türüne göre ara
    if (id) {
      componentRef = this.dialogCache.get(id);
    } else {
      componentRef = this.dialogTurCache.get(bilesen);
    }


    if (!componentRef) {
      // İlk defa açılıyor - yeni instance oluştur

      componentRef = createComponent(FikoDialogCore, {
        environmentInjector: this.injector
      });

      // Component'i DOM'a ekle
      this.appRef.attachView(componentRef.hostView);
      const domElem = (componentRef.hostView as any).rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);

      const dialogInstance = componentRef.instance;

      // Custom ID verilmişse onu kullan
      if (id) {
        dialogInstance.id_dialog = id;
      }

      const finalId = id || dialogInstance.id_dialog;

      // Cache'lere ekle
      this.dialogCache.set(finalId, componentRef);
      if (!id) {
        // ID verilmemişse component türüne göre de cache'le (geriye uyumluluk)
        this.dialogTurCache.set(bilesen, componentRef);
      }

      // Z-index takibi için tıklama olayı ekle
      this.dialogTiklamaOlayiEkle(dialogInstance);

      const originalKapa = dialogInstance.kapa.bind(dialogInstance);
      dialogInstance.kapa = () => {
        originalKapa();
        // Dialog kapandığında z-index takibinden kaldır
        this.dialogKapandi(dialogInstance);
        // Not: DOM'dan kaldırmıyoruz, sadece modal gizleniyor
        // Component state'i korunuyor
      };
    }

    // ⚠️ KRITIK: Eğer dialog zaten açıksa, TEKRAR AÇMA!
    // Sadece öne getir ve mevcut instance'ı döndür
    if (componentRef.instance.miAcik) {
      // Dialog'u öne getir
      const dialogElement = document.getElementById(componentRef.instance.id_dialog);
      if (dialogElement) {
        // overlayZIndexAl: Sayfadaki TÜM elementleri tarar ve en yüksek + 10 döndürür
        const yeniZIndex = this.overlayZIndexAl();
        this.dialogZIndexUygula(componentRef.instance, yeniZIndex);
        this.dialogTiklandi(componentRef.instance); // Internal takip için
        dialogElement.focus();
      }

      return componentRef.instance;
    }

    // Dialog kapalı - aç
    componentRef.instance.ac({ bilesen, ayarlar, parametreler, $event });

    // Dialog açıldığında z-index takibini güncelle
    const yeniZIndex = this.dialogAcildi(componentRef.instance);
    this.dialogZIndexUygula(componentRef.instance, yeniZIndex);

    return componentRef.instance;
  }

  /**
   * Her zaman YENİ dialog penceresi oluşturur (aynı component türünden birden fazla pencere)
   * Otomatik benzersiz ID üretir
   *
   * @returns Dialog instance (ID'ye erişmek için: dialog.id_dialog)
   */
  yeniAc({ bilesen, ayarlar = null, parametreler = null, $event = null }: {
    bilesen: Type<any>,
    ayarlar?: any,
    parametreler?: any,
    $event?: any
  }): FikoDialogCore {
    // Yeni instance oluştur
    const componentRef = createComponent(FikoDialogCore, {
      environmentInjector: this.injector
    });

    // Component'i DOM'a ekle
    this.appRef.attachView(componentRef.hostView);
    const domElem = (componentRef.hostView as any).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    const dialogInstance = componentRef.instance;

    // Benzersiz ID üret
    const yeniId = `${dialogInstance.id_dialog}_${++this.dialogIdSayaci}`;
    dialogInstance.id_dialog = yeniId;

    // ID cache'ine ekle
    this.dialogCache.set(yeniId, componentRef);

    // Z-index takibi için tıklama olayı ekle
    this.dialogTiklamaOlayiEkle(dialogInstance);

    // Dialog kapandığında sadece gizle, destroy etme
    const originalKapa = dialogInstance.kapa.bind(dialogInstance);
    dialogInstance.kapa = () => {
      originalKapa();
      // Dialog kapandığında z-index takibinden kaldır
      this.dialogKapandi(dialogInstance);
      // Not: DOM'dan kaldırmıyoruz, sadece modal gizleniyor
      // Component state'i korunuyor
    };

    // Dialog'u aç
    dialogInstance.ac({ bilesen, ayarlar, parametreler, $event });

    // Dialog açıldığında z-index takibini güncelle
    const yeniZIndex = this.dialogAcildi(dialogInstance);
    this.dialogZIndexUygula(dialogInstance, yeniZIndex);

    return dialogInstance;
  }

  /**
   * ID'ye göre dialog instance döndürür
   *
   * @param id - Dialog benzersiz ID'si
   * @returns Dialog instance veya null
   */
  idIleAl(id: string): FikoDialogCore | null {
    const componentRef = this.dialogCache.get(id);
    return componentRef ? componentRef.instance : null;
  }

  /**
   * Tüm açık dialogların ID listesini döndürür
   */
  tumDialogIdleri(): string[] {
    return Array.from(this.dialogCache.keys());
  }

  /**
   * Belirli bir component türünden kaç tane dialog açık olduğunu döndürür
   */
  componentTurundenAcikSayisi(bilesen: Type<any>): number {
    let sayac = 0;
    this.dialogCache.forEach((componentRef) => {
      if (componentRef.instance.bilesen === bilesen && componentRef.instance.miAcik) {
        sayac++;
      }
    });
    return sayac;
  }

  /**
   * Dialog'a tıklama olayı ekler
   */
  private dialogTiklamaOlayiEkle(dialogInstance: FikoDialogCore): void {
    setTimeout(() => {
      const dialogElement = document.getElementById(dialogInstance.id_dialog);
      if (dialogElement) {
        dialogElement.addEventListener('mousedown', (event) => {
          // Sadece dialog'un kendisine tıklandığında çalışsın
          if (event.target === dialogElement || dialogElement.contains(event.target as Node)) {
            const yeniZIndex = this.dialogTiklandi(dialogInstance);
            this.dialogZIndexUygula(dialogInstance, yeniZIndex);
          }
        });
      }
    }, 100);
  }

  /**
   * Dialog'a z-index değerini uygular
   * Semantic UI modal wrapper (parent) ve dialog element'e z-index uygular
   * !important kullanarak CSS override'ları geçersiz kılar
   */
  private dialogZIndexUygula(dialogInstance: FikoDialogCore, zIndex: number): void {
    const uygula = () => {
      const dialogElement = document.getElementById(dialogInstance.id_dialog);
      if (dialogElement) {
        dialogElement.style.setProperty('z-index', zIndex.toString(), 'important');

        const parent = dialogElement.parentElement;
        if (parent) {
          parent.style.setProperty('z-index', zIndex.toString(), 'important');
        }
      }
    };

    // 🚀 PERFORMANS: Triple setTimeout yerine hemen + bir rAF yeterli
    uygula();
    requestAnimationFrame(uygula);
  }

  // Belirli bir bileşen türü için dialog'u tamamen temizle
  temizle(bilesen: Type<any>) {
    const componentRef = this.dialogTurCache.get(bilesen);
    if (componentRef) {
      const dialogInstance = componentRef.instance;
      const dialogId = dialogInstance.id_dialog;

      // Önce kapat
      dialogInstance.kapa();

      // Sonra temizle
      setTimeout(() => {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
        this.dialogTurCache.delete(bilesen);
        this.dialogCache.delete(dialogId);

        // Z-index takibinden de kaldır
        this.dialogKapandi(dialogInstance);
      }, 300);
    }
  }

  /**
   * ID'ye göre dialog'u tamamen temizler (destroy)
   */
  idIleTemizle(id: string) {
    const componentRef = this.dialogCache.get(id);
    if (componentRef) {
      const dialogInstance = componentRef.instance;

      // Önce kapat
      dialogInstance.kapa();

      // Sonra temizle
      setTimeout(() => {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
        this.dialogCache.delete(id);

        // Z-index takibinden de kaldır
        this.dialogKapandi(dialogInstance);
      }, 300);
    }
  }

  // Tüm cache'i temizle
  tumunuTemizle() {
    this.dialogCache.forEach((componentRef, id) => {
      const dialogInstance = componentRef.instance;
      dialogInstance.kapa();
      setTimeout(() => {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();

        // Z-index takibinden de kaldır
        this.dialogKapandi(dialogInstance);
      }, 300);
    });
    this.dialogCache.clear();
    this.dialogTurCache.clear();
  }

  // Tüm açık dialogları kapat (ama destroy etme)
  tumunuKapat() {
    this.dialogCache.forEach(ref => {
      ref.instance.kapa();
    });
  }

  // Belirli bir dialog'u ID ile kapat
  idIleKapat(dialogId: string) {
    const componentRef = this.dialogCache.get(dialogId);
    if (componentRef) {
      componentRef.instance.kapa();
    }
  }
}
